import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { InputField } from '../../components/ui/InputField';
import { api, ApiError } from '../../lib/api';
import type { Group, RolesOverview, RolesOverviewMember } from '../../lib/types';

function memberLabel(m: RolesOverviewMember): string {
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return full || m.email || '—';
}

export function OrganizationGroupsSection({ isOrgAdmin }: { isOrgAdmin: boolean }) {
  const [overview, setOverview] = useState<RolesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setOverview(await api.getRolesOverview());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить группы.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!overview) {
    return (
      <Card title="Группы" className="mt-6">
        {error ? (
          <p className="text-xs font-medium text-status-warning">{error}</p>
        ) : (
          <p className="text-xs text-text-secondary">Загрузка…</p>
        )}
      </Card>
    );
  }

  return (
    <div className="mt-6">
      {error && <p className="mb-3 text-xs font-medium text-status-warning">{error}</p>}
      <GroupsCard overview={overview} isOrgAdmin={isOrgAdmin} onChange={load} onError={setError} />
    </div>
  );
}

function GroupsCard({
  overview,
  isOrgAdmin,
  onChange,
  onError,
}: {
  overview: RolesOverview;
  isOrgAdmin: boolean;
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setCreating(true);
    try {
      await api.createGroup(newGroupName.trim());
      setNewGroupName('');
      await onChange();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Не удалось создать группу.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card title="Группы">
      {isOrgAdmin && (
        <form onSubmit={handleCreate} className="mb-5 flex items-end gap-2">
          <div className="flex-1">
            <InputField
              label="Новая группа"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Например, «Операторы Дороги»"
            />
          </div>
          <Button type="submit" className="h-[38px] shrink-0 px-4" disabled={creating}>
            {creating ? '…' : 'Создать'}
          </Button>
        </form>
      )}

      {overview.groups.length === 0 ? (
        <p className="text-xs text-text-secondary">Группы ещё не созданы.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {overview.groups.map((g) => (
            <GroupRow key={g.id} group={g} overview={overview} isOrgAdmin={isOrgAdmin} onChange={onChange} onError={onError} />
          ))}
        </div>
      )}
    </Card>
  );
}

function GroupRow({
  group,
  overview,
  isOrgAdmin,
  onChange,
  onError,
}: {
  group: Group;
  overview: RolesOverview;
  isOrgAdmin: boolean;
  onChange: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [addMemberId, setAddMemberId] = useState('');
  const [grantWorkspaceId, setGrantWorkspaceId] = useState('');
  const [grantRoleCode, setGrantRoleCode] = useState('');
  const [busy, setBusy] = useState(false);

  const memberOf = (userId: string) => overview.members.find((m) => m.user_id === userId);
  const availableMembers = overview.members.filter((m) => !group.member_user_ids.includes(m.user_id));

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await onChange();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Не удалось выполнить действие.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteGroup = () => run(() => api.deleteGroup(group.id));
  const handleAddMember = () => {
    if (!addMemberId) return;
    run(() => api.addGroupMember(group.id, addMemberId)).then(() => setAddMemberId(''));
  };
  const handleRemoveMember = (userId: string) => run(() => api.removeGroupMember(group.id, userId));
  const handleGrant = () => {
    if (!grantWorkspaceId || !grantRoleCode) return;
    run(() => api.grantWorkspaceGroup(grantWorkspaceId, group.id, grantRoleCode)).then(() => {
      setGrantWorkspaceId('');
      setGrantRoleCode('');
    });
  };
  const handleRevoke = (workspaceId: string) => run(() => api.revokeWorkspaceGroup(workspaceId, group.id));

  return (
    <div className="rounded-xl border border-border-default p-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-text-primary">{group.name}</p>
        {isOrgAdmin && (
          <button type="button" onClick={handleDeleteGroup} disabled={busy} className="text-[10px] font-semibold text-status-danger">
            Удалить группу
          </button>
        )}
      </div>

      <div className="mt-3">
        <p className="text-[10px] font-medium text-text-secondary">Участники</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {group.member_user_ids.length === 0 && <span className="text-[11px] text-text-secondary">Нет участников</span>}
          {group.member_user_ids.map((uid) => {
            const m = memberOf(uid);
            return (
              <span
                key={uid}
                className="inline-flex items-center gap-1.5 rounded-full bg-bg-surface-secondary px-2.5 py-1 text-[10px] font-medium text-text-primary"
              >
                {m ? memberLabel(m) : uid}
                {isOrgAdmin && (
                  <button type="button" onClick={() => handleRemoveMember(uid)} disabled={busy} className="text-text-secondary hover:text-status-danger">
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
        {isOrgAdmin && availableMembers.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <select
              value={addMemberId}
              onChange={(e) => setAddMemberId(e.target.value)}
              className="h-8 rounded-md border border-border-default bg-bg-surface px-2 text-[11px] text-text-primary"
            >
              <option value="">Добавить участника…</option>
              {availableMembers.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {memberLabel(m)}
                </option>
              ))}
            </select>
            <button type="button" onClick={handleAddMember} disabled={busy || !addMemberId} className="text-[10px] font-semibold text-action-primary">
              Добавить
            </button>
          </div>
        )}
      </div>

      <div className="mt-3">
        <p className="text-[10px] font-medium text-text-secondary">Доступ к пространствам</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {group.grants.length === 0 && <span className="text-[11px] text-text-secondary">Нет доступа</span>}
          {group.grants.map((grant) => {
            const ws = overview.workspaces.find((w) => w.id === grant.workspace_id);
            const role = overview.roles.find((r) => r.code === grant.role_code);
            return (
              <span
                key={grant.workspace_id}
                className="inline-flex items-center gap-1.5 rounded-full bg-bg-surface-secondary px-2.5 py-1 text-[10px] font-medium text-text-primary"
              >
                {ws?.name ?? grant.workspace_id} — {role?.name ?? grant.role_code}
                {isOrgAdmin && (
                  <button type="button" onClick={() => handleRevoke(grant.workspace_id)} disabled={busy} className="text-text-secondary hover:text-status-danger">
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
        {isOrgAdmin && (
          <div className="mt-2 flex items-center gap-2">
            <select
              value={grantWorkspaceId}
              onChange={(e) => setGrantWorkspaceId(e.target.value)}
              className="h-8 rounded-md border border-border-default bg-bg-surface px-2 text-[11px] text-text-primary"
            >
              <option value="">Пространство…</option>
              {overview.workspaces.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}
                </option>
              ))}
            </select>
            <select
              value={grantRoleCode}
              onChange={(e) => setGrantRoleCode(e.target.value)}
              className="h-8 rounded-md border border-border-default bg-bg-surface px-2 text-[11px] text-text-primary"
            >
              <option value="">Роль…</option>
              {overview.roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleGrant}
              disabled={busy || !grantWorkspaceId || !grantRoleCode}
              className="text-[10px] font-semibold text-action-primary"
            >
              Выдать
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
